import { memo, useCallback, useState } from 'react';
import { Popover } from 'antd';
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { WorkOrderCalendarMode } from '../../../../enums/workOrderView';
import { MonthGrid, YearList } from '../../../../shared/components/PeriodPicker';
import styles from './WorkOrderCalendar.module.scss';

const PREV_ICON = <CaretLeftOutlined aria-hidden />;
const NEXT_ICON = <CaretRightOutlined aria-hidden />;

// Middle of the week label, between the month and year buttons ("19–25,", "29 – JULY 5,").
const getWeekSpanText = (start: Dayjs, end: Dayjs) => {
  if (start.year() !== end.year()) return `${start.format('D, YYYY')} – ${end.format('MMMM D')},`;
  if (start.month() !== end.month()) return `${start.date()} – ${end.format('MMMM D')},`;
  return `${start.date()}–${end.date()},`;
};

interface CalendarHeaderProps {
  mode: WorkOrderCalendarMode;
  rangeStart: Dayjs;
  rangeEnd: Dayjs;
  onStep: (direction: 1 | -1) => void;
  onMonthSelect: (month: number) => void;
  onYearSelect: (year: number) => void;
  onModeToggle: () => void;
}

const CalendarHeader = ({
  mode,
  rangeStart,
  rangeEnd,
  onStep,
  onMonthSelect,
  onYearSelect,
  onModeToggle,
}: CalendarHeaderProps) => {
  const [isMonthPickerOpen, setMonthPickerOpen] = useState(false);
  const [isYearPickerOpen, setYearPickerOpen] = useState(false);

  const isWeek = mode === WorkOrderCalendarMode.WEEK;
  const unit = isWeek ? 'week' : 'month';
  // A week crossing into a new year shows the later year on its button.
  const year = rangeEnd.year();

  const handlePrevClick = () => onStep(-1);
  const handleNextClick = () => onStep(1);

  // Stable so the memoised pickers skip re-renders while open.
  const handleMonthSelect = useCallback(
    (month: number) => {
      onMonthSelect(month);
      setMonthPickerOpen(false);
    },
    [onMonthSelect],
  );

  const handleYearSelect = useCallback(
    (selectedYear: number) => {
      onYearSelect(selectedYear);
      setYearPickerOpen(false);
    },
    [onYearSelect],
  );

  return (
    <header className={styles.calendarHeader}>
      <div className={styles.calendarHeaderNav}>
        <button type="button" className={styles.calendarHeaderArrow} aria-label={`Previous ${unit}`} onClick={handlePrevClick}>
          {PREV_ICON}
        </button>
        <h2 className={styles.calendarHeaderTitle} aria-live="polite">
          <Popover
            open={isMonthPickerOpen}
            onOpenChange={setMonthPickerOpen}
            trigger="click"
            placement="bottom"
            arrow={false}
            destroyTooltipOnHide
            overlayClassName={styles.calendarPickerOverlay}
            content={<MonthGrid value={rangeStart.month()} onSelect={handleMonthSelect} />}
          >
            <button type="button" className={styles.calendarHeaderPeriod} aria-haspopup="true" aria-expanded={isMonthPickerOpen}>
              {rangeStart.format('MMMM')}
            </button>
          </Popover>
          {isWeek ? <span>{getWeekSpanText(rangeStart, rangeEnd)}</span> : null}
          <Popover
            open={isYearPickerOpen}
            onOpenChange={setYearPickerOpen}
            trigger="click"
            placement="bottom"
            arrow={false}
            destroyTooltipOnHide
            overlayClassName={styles.calendarPickerOverlay}
            content={<YearList value={year} onSelect={handleYearSelect} />}
          >
            <button type="button" className={styles.calendarHeaderPeriod} aria-haspopup="true" aria-expanded={isYearPickerOpen}>
              {year}
            </button>
          </Popover>
        </h2>
        <button type="button" className={styles.calendarHeaderArrow} aria-label={`Next ${unit}`} onClick={handleNextClick}>
          {NEXT_ICON}
        </button>
      </div>
      <button
        type="button"
        className={styles.calendarModeToggle}
        aria-label={isWeek ? 'Switch to month view' : 'Switch to week view'}
        onClick={onModeToggle}
      />
    </header>
  );
};

export default memo(CalendarHeader);
